import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

const tagVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent font-medium whitespace-nowrap transition-[color,background-color,border-color,box-shadow] focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        success: 'bg-success text-success-foreground [a&]:hover:bg-success/90',
        destructive:
          'bg-destructive text-destructive-foreground focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90',
        accent: 'bg-accent text-accent-foreground [a&]:hover:bg-accent/70',
        promo: 'bg-brand-cream text-brand-maroon [a&]:hover:bg-brand-cream/90',
        outline:
          'border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        ghost: '[a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        link: 'text-link link-hover-underline',
      },
      size: {
        sm: 'px-2 py-0.5 text-sm [&>svg]:size-3',
        md: 'px-2.5 py-1 text-sm [&>svg]:size-3.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  },
)

type TagProps = React.ComponentProps<'span'> &
  VariantProps<typeof tagVariants> & {
    asChild?: boolean
    /** Leading status dot, tinted to the tag's current text color. */
    dot?: boolean
    /** Renders a trailing ✕ button (removable pill). Ignored when `asChild`. */
    onRemove?: () => void
    /** Accessible label for the remove button (e.g. `Remove ${label}`). */
    removeLabel?: string
  }

/**
 * Tag — the text chip / pill family (category, status, attribute, promo, removable
 * filter pills). Distinct from `Badge` (`@/components/portal/badge`), which is the
 * branded SVG-designation family.
 */
function Tag({
  className,
  variant = 'default',
  size = 'sm',
  asChild = false,
  dot = false,
  onRemove,
  removeLabel = 'Remove',
  children,
  ...props
}: TagProps) {
  const Comp = asChild ? Slot.Root : 'span'

  // Slot requires a single child — dot/remove decorations only apply to the span form.
  if (asChild) {
    return (
      <Comp
        data-slot="tag"
        data-variant={variant}
        className={cn(tagVariants({ variant, size }), className)}
        {...props}
      >
        {children}
      </Comp>
    )
  }

  return (
    <Comp
      data-slot="tag"
      data-variant={variant}
      className={cn(tagVariants({ variant, size }), onRemove && 'pr-1', className)}
      {...props}
    >
      {dot && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current opacity-80" />}
      {children}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className="focus-visible:ring-ring/50 -mr-0.5 ml-0.5 grid size-4 shrink-0 place-items-center rounded-full opacity-70 transition-opacity outline-none hover:opacity-100 focus-visible:ring-2"
        >
          <X className="size-3" strokeWidth={2.5} />
        </button>
      )}
    </Comp>
  )
}

export { Tag, tagVariants }
export type { TagProps }
